/**
 * Market Manager - Sistema automático de gestión de markets
 * Detecta cambios de market y sincroniza automáticamente los attributes del carrito
 */

class MarketManager {
  constructor() {
    this.initialized = false;
    this.currentMarket = window.originalMarketHandle || 'eu';
    this.observers = [];
    this.checkInterval = null;
    
    this.init();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  init() {
    if (this.initialized) return;
        
    this.setupGlobalFunctions();
    this.setupObservers();
    this.setupPeriodicCheck();
    
    // Verificar y actualizar carrito al inicializar
    setTimeout(() => {
      this.checkCartConsistency();
    }, 1000);
    
    this.initialized = true;
  }



  // ============================================================================
  // GLOBAL FUNCTIONS
  // ============================================================================

  setupGlobalFunctions() {
    // Función principal para actualizar variables de market
    window.updateMarketVariables = (marketHandle) => {
      const normalized = marketHandle ? marketHandle.toLowerCase() : 'eu';
      const countryMarket = normalized.toUpperCase();
      const attributeMarket = (countryMarket === 'EU' || countryMarket === 'ROW') ? 'IB' : 'IBPI';
      
      const changed = window.market !== attributeMarket || window.countryMarket !== countryMarket;
      
      if (changed) {
        window.market = attributeMarket;
        window.countryMarket = countryMarket;
        window.originalMarketHandle = normalized;
        
        // console.log(`[MarketManager] updated: ${attributeMarket} (${countryMarket})`);
        
        // Disparar evento
        document.dispatchEvent(new CustomEvent('marketChanged', {
          detail: { market: attributeMarket, countryMarket, originalHandle: normalized }
        }));
        
        // Auto-actualizar carrito si tiene productos
        if (window.marketManager) {
          window.marketManager.autoUpdateCart();
        } else {
          console.warn('[MarketManager] instance not found!');
        }
      }
    };

    // Función para obtener attributes actuales
    window.getMarketAttributes = () => ({
      market: window.market || 'IB',
      countryMarket: window.countryMarket || 'EU'
    });

    // Función para actualizar carrito
    window.updateCartMarketAttributes = () => this.updateCartAttributes();
  }

  // ============================================================================
  // CART MANAGEMENT
  // ============================================================================

  async updateCartAttributes() {
    const attributes = window.getMarketAttributes();
    
    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const cart = await response.json();
      const success = cart.attributes?.market === attributes.market;
      
      if (success) {
        console.log(`[MarketManager] Cart updated: ${attributes.market} (${attributes.countryMarket})`);
      } else {
        console.warn('[MarketManager] Cart update may have failed');
      }

      document.dispatchEvent(new CustomEvent('cartMarketUpdated', {
        detail: { cart, attributes }
      }));

      return cart;

    } catch (error) {
      console.error('[MarketManager] Cart update failed:', error.message);
      return this.updateCartAttributesAlternative(attributes);
    }
  }

  async updateCartAttributesAlternative(attributes) {
    try {
      const formData = new FormData();
      Object.keys(attributes).forEach(key => {
        formData.append(`attributes[${key}]`, attributes[key]);
      });

      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const cart = await response.json();
      console.log('[MarketManager] Cart updated (alternative method)');
      
      document.dispatchEvent(new CustomEvent('cartMarketUpdated', {
        detail: { cart, attributes }
      }));

      return cart;

    } catch (error) {
      console.error('[MarketManager] Alternative update failed:', error.message);
      throw error;
    }
  }

  async autoUpdateCart() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      const cart = await response.json();
      
      if (cart.item_count > 0) {
        const attributes = window.getMarketAttributes();
        console.log(`[MarketManager] Auto-updating cart attributes to: ${attributes.market} (${attributes.countryMarket})`);
        await this.updateCartAttributes();
      }
    } catch (error) {
      console.warn('[MarketManager] Auto-update failed:', error.message);
    }
  }

  async checkCartConsistency() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      const cart = await response.json();
      
      if (cart.item_count > 0) {
        const currentAttributes = window.getMarketAttributes();
        const cartMarket = cart.attributes?.market;
        
        if (cartMarket !== currentAttributes.market) {
          await this.updateCartAttributes();
        } 
      } 
    } catch (error) {
      console.warn('[MarketManager] Cart consistency check failed:', error.message);
    }
  }

  // ============================================================================
  // OBSERVERS & DETECTION
  // ============================================================================

  setupObservers() {
    // Observer para cambios en el DOM
    // Se agrupan todas las mutaciones en una única comprobación diferida para no
    // bloquear el hilo principal durante interacciones (INP). No se observa 'class'
    // porque cambia constantemente (scroll, hover, drawers) y no afecta al market.
    const observer = new MutationObserver(() => this.scheduleMarketCheck());

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-market', 'data-country']
    });

    this.observers.push(observer);

    // Observer para cambios en localStorage/sessionStorage
    const originalSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function(key, value) {
      if (key.includes('market') || key.includes('country')) {
        setTimeout(() => window.marketManager?.checkForMarketChanges(), 100);
      }
      originalSetItem.apply(this, arguments);
    };

    // Observer para cambios en window.location
    let lastUrl = location.href;
    new MutationObserver(() => {
      const currentUrl = location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        setTimeout(() => this.checkForMarketChanges(), 200);
      }
    }).observe(document, { subtree: true, childList: true });
  }

  setupPeriodicCheck() {
    // Verificación cada 30 segundos
    this.checkInterval = setInterval(() => {
      this.checkForMarketChanges();
    }, 30000);
  }

  scheduleMarketCheck() {
    if (this.marketCheckPending) return;
    this.marketCheckPending = true;

    setTimeout(() => {
      this.marketCheckPending = false;
      this.checkForMarketChanges();
    }, 200);
  }

  checkForMarketChanges() {
    const detectedMarket = this.detectCurrentMarket();
    if (detectedMarket && detectedMarket !== this.currentMarket) {
      console.log(`[MarketManager] Market change detected: ${this.currentMarket} → ${detectedMarket}`);
      this.currentMarket = detectedMarket;
      window.updateMarketVariables(detectedMarket);
    }
  }

  detectCurrentMarket() {
    // Métodos de detección por prioridad
    const methods = [
      () => document.querySelector('[data-market]')?.dataset.market,
      () => document.querySelector('.market-selector [data-value]')?.dataset.value,
      () => window.Shopify?.shop?.market_handle,
      () => window.originalMarketHandle,
      () => this.extractMarketFromUrl(),
      () => localStorage.getItem('market'),
      () => sessionStorage.getItem('market')
    ];

    for (const method of methods) {
      try {
        const result = method();
        if (result && typeof result === 'string') {
          return result.toLowerCase();
        }
      } catch (e) {
        // Ignorar errores y continuar
      }
    }

    return null;
  }

  extractMarketFromUrl() {
    const url = window.location.href;
    const patterns = [
      /\/markets\/([a-z]{2})/i,
      /\?market=([a-z]{2})/i,
      /&market=([a-z]{2})/i,
      /\/([a-z]{2})\/(?:products|collections|pages)/i
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1].toLowerCase();
    }

    return null;
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  destroy() {
    // Limpiar observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];

    // Limpiar interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    // Limpiar funciones globales
    delete window.updateMarketVariables;
    delete window.getMarketAttributes;
    delete window.updateCartMarketAttributes;

    this.initialized = false;
    console.log('[MarketManager] destroyed');
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.marketManager = new MarketManager();
  });
} else {
  window.marketManager = new MarketManager();
}